import { useState, useMemo, useCallback } from 'react'
import { Search, X, ChevronLeft, ChevronRight } from 'lucide-react'
import { Modal } from '../../ui/Modal'
import { getAllIcons, getIconComponent } from './iconLibraries'

interface IconPickerModalProps {
  isOpen: boolean
  onClose: () => void
  onSelect: (iconName: string) => void
  selectedIcon?: string
}

const ICONS_PER_PAGE = 60

export function IconPickerModal({
  isOpen,
  onClose,
  onSelect,
  selectedIcon,
}: IconPickerModalProps) {
  const [searchTerm, setSearchTerm] = useState('')
  const [currentPage, setCurrentPage] = useState(1)

  const allIcons = useMemo(() => {
    return getAllIcons()
  }, [])

  const filteredIcons = useMemo(() => {
    if (!searchTerm.trim()) return allIcons

    const term = searchTerm.toLowerCase().trim()
    return allIcons.filter((name) => name.toLowerCase().includes(term))
  }, [allIcons, searchTerm])

  const paginatedIcons = useMemo(() => {
    const startIndex = (currentPage - 1) * ICONS_PER_PAGE
    const endIndex = startIndex + ICONS_PER_PAGE
    return filteredIcons.slice(startIndex, endIndex)
  }, [filteredIcons, currentPage])

  const totalPages = useMemo(() => {
    return Math.ceil(filteredIcons.length / ICONS_PER_PAGE)
  }, [filteredIcons.length])

  const handleSelectIcon = useCallback(
    (iconName: string) => {
      onSelect(iconName)
      onClose()
      setSearchTerm('')
      setCurrentPage(1)
    },
    [onSelect, onClose]
  )

  const handleSearchChange = useCallback((value: string) => {
    setSearchTerm(value)
    setCurrentPage(1)
  }, [])

  const handlePreviousPage = useCallback(() => {
    setCurrentPage((prev) => Math.max(1, prev - 1))
  }, [])

  const handleNextPage = useCallback(() => {
    setCurrentPage((prev) => Math.min(totalPages, prev + 1))
  }, [totalPages])

  const handleClose = useCallback(() => {
    setSearchTerm('')
    setCurrentPage(1)
    onClose()
  }, [onClose])

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title="Selecionar Ícone" size="4xl">
      <div className="flex flex-col" style={{ maxHeight: 'calc(100vh - 200px)' }}>
        <div className="flex flex-col gap-4 pb-4 border-b border-gray-200 mb-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              placeholder="Buscar ícone..."
              value={searchTerm}
              onChange={(e) => handleSearchChange(e.target.value)}
              className="w-full pl-10 pr-10 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-transparent bg-white"
              autoFocus
            />
            {searchTerm && (
              <button
                type="button"
                onClick={() => handleSearchChange('')}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </div>

          <div className="text-sm text-gray-600">
            {searchTerm ? (
              <span>
                {filteredIcons.length} ícone{filteredIcons.length !== 1 ? 's' : ''} encontrado
                {filteredIcons.length !== 1 ? 's' : ''}
              </span>
            ) : (
              <span>
                {allIcons.length} ícone{allIcons.length !== 1 ? 's' : ''} disponível
                {allIcons.length !== 1 ? 'is' : ''}
              </span>
            )}
          </div>
        </div>

        <div className="flex-1 overflow-y-auto py-4 min-h-0">
          {paginatedIcons.length === 0 ? (
            <div className="text-center py-12">
              <div className="text-lg font-medium text-gray-900 mb-2">Nenhum ícone encontrado</div>
              <div className="text-sm text-gray-500">Tente buscar com outro termo</div>
            </div>
          ) : (
            <div className="grid grid-cols-6 gap-3">
              {paginatedIcons.map((iconName) => {
                const IconComponent = getIconComponent(iconName)
                
                if (!IconComponent) {
                  return (
                    <div
                      key={iconName}
                      className="p-4 rounded-lg border-2 border-red-200 bg-red-50 flex flex-col items-center justify-center"
                      title={`Ícone "${iconName}" não encontrado`}
                    >
                      <span className="text-xs text-red-600 font-medium mb-1">?</span>
                      <span className="text-[10px] text-red-500 truncate w-full text-center">{iconName}</span>
                    </div>
                  )
                }

                const isSelected = selectedIcon === iconName

                return (
                  <button
                    key={iconName}
                    type="button"
                    onClick={() => handleSelectIcon(iconName)}
                    className={`group relative flex items-center justify-center p-4 rounded-lg border-2 transition-all duration-200 ${
                      isSelected
                        ? 'border-brand-500 bg-brand-50 shadow-sm scale-105'
                        : 'border-gray-200 hover:border-brand-300 hover:bg-gray-50 hover:shadow-sm'
                    }`}
                    title={iconName}
                  >
                    <IconComponent
                      className={`w-6 h-6 transition-colors ${
                        isSelected ? 'text-brand-600' : 'text-gray-700 group-hover:text-brand-600'
                      }`}
                    />
                  </button>
                )
              })}
            </div>
          )}
        </div>

        {totalPages > 1 && (
          <div className="flex items-center justify-between pt-4 border-t border-gray-200">
            <button
              type="button"
              onClick={handlePreviousPage}
              disabled={currentPage === 1}
              className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              <ChevronLeft className="w-4 h-4" />
              Anterior
            </button>

            <div className="text-sm text-gray-700">
              Página <span className="font-medium">{currentPage}</span> de{' '}
              <span className="font-medium">{totalPages}</span>
            </div>

            <button
              type="button"
              onClick={handleNextPage}
              disabled={currentPage === totalPages}
              className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              Próximo
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        )}
      </div>
    </Modal>
  )
}
